import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import configService, { BookingStatus } from "../services/config.service";

interface BookingStatusContextValue {
  bookingEnabled: boolean;
  bookingClosedMessage: string;
  pricePerAcademicYear?: number;
  loading: boolean;
  refresh: () => void;
}

const DEFAULT_CLOSED_MESSAGE =
  "Booking is currently closed. Please check back later or contact the hostel manager.";

const BookingStatusContext = createContext<BookingStatusContextValue>({
  bookingEnabled: true,
  bookingClosedMessage: DEFAULT_CLOSED_MESSAGE,
  pricePerAcademicYear: undefined,
  loading: true,
  refresh: () => {},
});

export function BookingStatusProvider({ children }: { children: ReactNode }) {
  // Fail open: assume booking is enabled until we learn otherwise, so a failed
  // fetch never blocks the UI or shows a false "closed" banner.
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [bookingClosedMessage, setBookingClosedMessage] = useState(DEFAULT_CLOSED_MESSAGE);
  const [pricePerAcademicYear, setPricePerAcademicYear] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    configService
      .getBookingStatus()
      .then((status: BookingStatus) => {
        setBookingEnabled(status.bookingEnabled);
        setBookingClosedMessage(status.bookingClosedMessage || DEFAULT_CLOSED_MESSAGE);
        setPricePerAcademicYear(status.pricePerAcademicYear);
      })
      .catch((error) => {
        console.error("Failed to load booking status:", error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BookingStatusContext.Provider
      value={{ bookingEnabled, bookingClosedMessage, pricePerAcademicYear, loading, refresh: load }}
    >
      {children}
    </BookingStatusContext.Provider>
  );
}

export function useBookingStatus() {
  return useContext(BookingStatusContext);
}
